import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { CancelButton } from 'components/CancelButton';
import { DeleteButton } from 'components/DeleteButton';
import { Panel } from 'components/Panel';
import { tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { capitalize, first, isArray, isEmpty, isNullish, partition } from 'remeda';
import { useDeleteContactPerson } from 'shared/hooks/use-delete-contact-person';
import type { ContactPerson } from 'shared/hooks/use-load-contact-person';
import { useLoadContactPersons } from 'shared/hooks/use-load-contact-persons';
import { PopoverInfo } from '../PopoverInfo';
import { ContactPersonModal } from './components/ContactPersonModal';

export const ContactPersonPanel = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const debtor = useDebtor();

  const { contactPersons, isContactPersonsInitialLoading } = useLoadContactPersons({
    debtorId: debtor.id,
  });
  const { deleteContactPerson } = useDeleteContactPerson();

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [isDeleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const [selectedContactPerson, setSelectedContactPerson] = useState<ContactPerson | null>(null);
  const [deletedContactPerson, setDeletedContactPerson] = useState<ContactPerson | null>(null);

  const openContactPerson = (contactPerson: ContactPerson) => {
    setSelectedContactPerson(contactPerson);
    openModal();
  };

  const createContactPerson = () => {
    history.push(`/clients/${debtor.id}/contact-persons/new`);
  };

  const editContactPerson = (id: ContactPerson['id']) => {
    history.push(`/clients/${debtor.id}/contact-persons/${id}/edit`);
  };

  const handleDeleteContactPerson = () => {
    closeDeleteModal();
    if (isNullish(deletedContactPerson)) return;
    deleteContactPerson({ debtorId: debtor.id, contactPersonId: deletedContactPerson?.id });
  };

  // Sort contact persons to show main contact first, then preserve API order for the rest
  const [mainContacts, otherContacts] = partition(
    contactPersons.data,
    contact => contact.main_contact,
  );
  const sortedContactPersons = [...mainContacts, ...otherContacts];

  const contactPersonRows = sortedContactPersons.map(contactPerson => (
    <Table.Tr
      key={contactPerson.id}
      onClick={() => openContactPerson(contactPerson)}
      style={{ cursor: 'pointer' }}
    >
      <Table.Td>
        <Stack gap={0}>
          <Group gap="space2">
            <Text c="gray.10" fw={500}>{`${getFullName(contactPerson)}`}</Text>
            {contactPerson.main_contact && (
              <Badge color="coral" radius="sm" size="xs" variant="outline">
                {t(tKeys.MAIN)}
              </Badge>
            )}
          </Group>
          <Text size="sm">{contactPerson.job_title}</Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap={0}>
          {contactPerson.emails?.[0] ? (
            <Group
              wrap="nowrap"
              maw="80%"
              gap={4}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Tooltip label={contactPerson.emails[0]} withArrow openDelay={500}>
                <Text truncate c="gray.10">
                  {contactPerson.emails[0]}
                </Text>
              </Tooltip>
              <PopoverInfo items={contactPerson.emails} />
            </Group>
          ) : (
            <Text c="gray.10">N/A</Text>
          )}
          {isArray(contactPerson.phones) &&
            contactPerson.phones.length > 0 &&
            (() => {
              const firstPhone = first(contactPerson.phones);
              return firstPhone ? <Text>{firstPhone.number}</Text> : null;
            })()}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Group gap={4} w={300}>
          {contactPerson.notifications?.map(notification => (
            <Badge variant="default" radius="sm" key={notification}>
              {t((tKeys.CONTACT_PERSON.NOTIFICATIONS as any)[notification.toUpperCase()])}
            </Badge>
          ))}
        </Group>
      </Table.Td>
      <Table.Td align="right">
        <Group gap="space3">
          <ActionIcon
            variant="subtle"
            color="purple.6"
            size="input-xs"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              editContactPerson(contactPerson.id);
            }}
          >
            <IconPencil stroke={1.5} size={18} />
          </ActionIcon>
          <ActionIcon
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              openDeleteModal();
              setDeletedContactPerson(contactPerson);
            }}
            variant="subtle"
            color="red"
            size="input-xs"
          >
            <IconTrash stroke={1.5} size={16} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const AddButton = () => (
    <Button
      variant="default"
      size="compact-sm"
      rightSection={<IconPlus size={16} stroke={1.5} />}
      onClick={createContactPerson}
    >
      {t(tKeys.ACTIONS.ADD)}
    </Button>
  );

  if (isContactPersonsInitialLoading) return <Loader />;

  if (isEmpty(contactPersons.data) && !isContactPersonsInitialLoading) {
    return (
      <Panel title={t(tKeys.CONTACT_PERSON.TITLE)} topRight={<AddButton />} withBorder>
        <Center>
          <Text fs="italic" c="dimmed">
            {t(tKeys.CONTACT_PERSON.NO_CONTACT_PERSON)}
          </Text>
        </Center>
      </Panel>
    );
  }

  return (
    <>
      <Panel title={t(tKeys.CONTACT_PERSON.TITLE)} topRight={<AddButton />} withBorder>
        <Card radius={8} p={0}>
          <Table>
            <Table.Tbody>{contactPersonRows}</Table.Tbody>
          </Table>
        </Card>
      </Panel>
      <ContactPersonModal
        contactPerson={selectedContactPerson}
        isModalOpened={isModalOpened}
        closeModal={closeModal}
      />
      <Modal
        closeButtonProps={{
          tabIndex: -1,
        }}
        opened={isDeleteModalOpened}
        onClose={closeDeleteModal}
        title={`${t(tKeys.DELETE)} ${getFullName(deletedContactPerson)}`}
      >
        <Text>
          {t(tKeys.AYS.DELETE_CONTACT_PERSON_WITH_NAME, {
            name: getFullName(deletedContactPerson),
          })}
        </Text>
        <Group mt="md" justify="end">
          <CancelButton onClick={closeDeleteModal} />
          <DeleteButton onClick={handleDeleteContactPerson} />
        </Group>
      </Modal>
    </>
  );
};

function getFullName(contactPerson: ContactPerson | null) {
  if (isNullish(contactPerson)) return '';
  return `${capitalize(contactPerson.first_name)} ${capitalize(contactPerson.last_name)}`;
}
